import { apiJson, apiVoid } from './http';
import type {
  ActivateCodelistResult,
  ActiveCodelist,
  ImportMappingsRequest,
  ImportMappingsResult,
  MappingImportMode,
  MappingsDocument,
  AdminFlowisAccount,
  BrokenMappings,
  CallerContext,
  CodelistDiff,
  CodelistVersion,
  CodelistVersionDetail,
  CommunicationLogEntry,
  CommunicationLogFilter,
  CommunicationLogListItem,
  CreateAccountRequest,
  CreateFlowisAccountRequest,
  CreateRemoteAccountRequest,
  CreateTenantRequest,
  FileListFilter,
  FlowisAccount,
  FlowisAvailability,
  FlowisDocumentQuery,
  FlowisDocumentSummary,
  FlowisPagedResponse,
  FlowisPeppolParticipant,
  FlowisReceivedDocument,
  FlowisSentDocument,
  ImportCodelistRequest,
  InboundAckResult,
  InboundCheckResult,
  InboundDocument,
  InboundDownloadOptions,
  InboundDownloadResult,
  MappingKind,
  OperationListFilter,
  PagedResponse,
  RemoteFlowisAccount,
  ReplaceCodelistEntriesRequest,
  SendOperation,
  SendOperationListItem,
  SendToFlowisRequest,
  SetAccountCredentialRequest,
  StandardCode,
  StoredFile,
  StoredFileDetail,
  StoredFileListItem,
  SubmitResult,
  TenantDetail,
  TenantHealth,
  TenantMapping,
  TenantProvisioningScript,
  TenantProvisioningScriptRequest,
  TenantSummary,
  UpdateFlowisAccountRequest,
  UpdateTenantRequest,
  UploadDocumentResult,
  UpsertTenantMappingRequest,
} from './types';

// ---------------------------------------------------------------------------
// Caller context
// ---------------------------------------------------------------------------

export function getCallerContext(signal?: AbortSignal): Promise<CallerContext> {
  return apiJson<CallerContext>('/tenant', signal ? { signal } : {});
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export function listAccounts(signal?: AbortSignal): Promise<FlowisAccount[]> {
  return apiJson<FlowisAccount[]>('/account', signal ? { signal } : {});
}

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

export function listFiles(
  filter: FileListFilter,
  signal?: AbortSignal,
): Promise<PagedResponse<StoredFileListItem>> {
  return apiJson<PagedResponse<StoredFileListItem>>('/file', {
    query: {
      page: filter.page,
      pageSize: filter.pageSize,
      sortBy: filter.sortBy,
      sortDirection: filter.sortDirection,
      kinds: filter.kinds,
      sources: filter.sources,
      inUse: filter.inUse,
      search: filter.search,
    },
    ...(signal ? { signal } : {}),
  });
}

export function getFileDetail(fileGid: string, signal?: AbortSignal): Promise<StoredFileDetail> {
  return apiJson<StoredFileDetail>(`/file/${fileGid}/detail`, signal ? { signal } : {});
}

export interface UploadDocumentInput {
  file: File;
  kind: 'BisDocument' | 'IsdocDocument';
  idempotencyKey: string;
}

export function uploadDocument(input: UploadDocumentInput): Promise<UploadDocumentResult> {
  const body = new FormData();
  body.append('file', input.file);
  body.append('Kind', input.kind);

  return apiJson<UploadDocumentResult>('/file/document', {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': input.idempotencyKey },
  });
}

export interface UploadAttachmentInput {
  file: File;
  idempotencyKey: string;
}

export function uploadAttachment(input: UploadAttachmentInput): Promise<StoredFile> {
  const body = new FormData();
  body.append('file', input.file);

  return apiJson<StoredFile>('/file/attachment', {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': input.idempotencyKey },
  });
}

export function replaceFileContent(fileGid: string, file: File): Promise<UploadDocumentResult> {
  const body = new FormData();
  body.append('file', file);

  return apiJson<UploadDocumentResult>(`/file/${fileGid}/content`, { method: 'PUT', body });
}

export function deleteFile(fileGid: string): Promise<void> {
  return apiVoid(`/file/${fileGid}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

export function listOperations(
  filter: OperationListFilter,
  signal?: AbortSignal,
): Promise<PagedResponse<SendOperationListItem>> {
  return apiJson<PagedResponse<SendOperationListItem>>('/operation', {
    query: {
      page: filter.page,
      pageSize: filter.pageSize,
      sortBy: filter.sortBy,
      sortDirection: filter.sortDirection,
      statuses: filter.statuses,
      participantId: filter.participantId,
      externalDocumentId: filter.externalDocumentId,
      submittedFrom: filter.submittedFrom,
      submittedTo: filter.submittedTo,
      mine: filter.mine,
    },
    ...(signal ? { signal } : {}),
  });
}

export function getOperation(
  operationGid: string,
  refresh = false,
  signal?: AbortSignal,
): Promise<SendOperation> {
  return apiJson<SendOperation>(`/operation/${operationGid}`, {
    query: refresh ? { refresh: true } : {},
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Communication log
// ---------------------------------------------------------------------------

export function listCommunicationLog(
  filter: CommunicationLogFilter,
  signal?: AbortSignal,
): Promise<PagedResponse<CommunicationLogListItem>> {
  return apiJson<PagedResponse<CommunicationLogListItem>>('/communication-log', {
    query: {
      page: filter.page,
      pageSize: filter.pageSize,
      sortBy: filter.sortBy,
      sortDirection: filter.sortDirection,
      search: filter.search,
      channels: filter.channels,
      outcomes: filter.outcomes,
      recordedFrom: filter.recordedFrom,
      recordedTo: filter.recordedTo,
    },
    ...(signal ? { signal } : {}),
  });
}

export function getCommunicationLogEntry(
  entryGid: string,
  signal?: AbortSignal,
): Promise<CommunicationLogEntry> {
  return apiJson<CommunicationLogEntry>(`/communication-log/${entryGid}`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * The route carries the sending account, split at the colon of its Peppol participant identifier:
 * `0245:2120147656` addresses `/participant/0245/2120147656`. It is the only way an account is named,
 * because it is the only identifier a sender has without being told one of ours first.
 */
function participantPath(participantId: string): string {
  const separator = participantId.indexOf(':');
  if (separator <= 0) throw new Error(`'${participantId}' is not a scheme:code participant identifier.`);

  return `/participant/${encodeURIComponent(participantId.slice(0, separator))}/${encodeURIComponent(
    participantId.slice(separator + 1),
  )}`;
}

export interface SubmitFileInput {
  /** The sending account's Peppol participant identifier, scheme:code. */
  participantId: string;
  file: File;
  kind: 'BisDocument' | 'IsdocDocument';
  attachments: File[];
  /** The caller's own identifier; repeating it answers with the first operation, not a second send. */
  externalDocumentId?: string;
  /** SHA-256 of the document as hex; the API refuses content that does not hash to it. */
  sha256?: string;
  idempotencyKey: string;
}

/**
 * The one-call submission: stores the document with its attachments, validates it and queues it
 * through the participant's account — the same path a machine caller takes over submit/file. A 200
 * may still carry warnings in `validation`; a refusal is a 422 whose failures arrive in the error.
 */
export function submitFile(input: SubmitFileInput): Promise<SubmitResult> {
  const body = new FormData();
  body.append('file', input.file);
  body.append('Kind', input.kind);
  if (input.sha256) body.append('Sha256', input.sha256);
  if (input.externalDocumentId) body.append('ExternalDocumentId', input.externalDocumentId);
  for (const attachment of input.attachments) body.append('attachments', attachment);

  return apiJson<SubmitResult>(`${participantPath(input.participantId)}/submit/file`, {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': input.idempotencyKey },
  });
}

export function sendOperation(participantId: string, request: SendToFlowisRequest): Promise<SendOperation> {
  return apiJson<SendOperation>(`${participantPath(participantId)}/send`, {
    method: 'POST',
    body: request,
  });
}

// ---------------------------------------------------------------------------
// Flowis passthrough
// ---------------------------------------------------------------------------

/**
 * Whether Flowis is answering. Served from what the API's own background work last saw, so it costs
 * the gate nothing and stays cheap to poll.
 */
export function getFlowisAvailability(signal?: AbortSignal): Promise<FlowisAvailability> {
  return apiJson<FlowisAvailability>('/flowis/availability', { ...(signal ? { signal } : {}) });
}

/**
 * Asks the public Peppol Directory, through the gate, who is registered under `participantId`
 * (scheme:id, e.g. 0245:2120147656). An empty list is the real answer "nobody", not a failure.
 */
export function peppolLookup(
  participantId: string,
  signal?: AbortSignal,
): Promise<FlowisPeppolParticipant[]> {
  return apiJson<FlowisPeppolParticipant[]>('/flowis/peppol-lookup', {
    query: { participantId },
    ...(signal ? { signal } : {}),
  });
}

function documentQuery(query: FlowisDocumentQuery) {
  return {
    offset: query.offset,
    limit: query.limit,
    status: query.status,
    fromDate: query.fromDate,
    toDate: query.toDate,
    downloaded: query.downloaded,
    selfbill: query.selfbill,
  };
}

export function listSentDocuments(
  accountGid: string,
  query: FlowisDocumentQuery,
  signal?: AbortSignal,
): Promise<FlowisPagedResponse<FlowisDocumentSummary>> {
  return apiJson<FlowisPagedResponse<FlowisDocumentSummary>>(`/flowis/accounts/${accountGid}/sent`, {
    query: documentQuery(query),
    ...(signal ? { signal } : {}),
  });
}

export function getSentDocument(
  accountGid: string,
  documentId: string,
  signal?: AbortSignal,
): Promise<FlowisSentDocument> {
  return apiJson<FlowisSentDocument>(
    `/flowis/accounts/${accountGid}/sent/${documentId}`,
    signal ? { signal } : {},
  );
}

export function listReceivedDocuments(
  accountGid: string,
  query: FlowisDocumentQuery,
  signal?: AbortSignal,
): Promise<FlowisPagedResponse<FlowisDocumentSummary>> {
  return apiJson<FlowisPagedResponse<FlowisDocumentSummary>>(`/flowis/accounts/${accountGid}/received`, {
    query: documentQuery(query),
    ...(signal ? { signal } : {}),
  });
}

export function getReceivedDocument(
  accountGid: string,
  documentId: string,
  signal?: AbortSignal,
): Promise<FlowisReceivedDocument> {
  return apiJson<FlowisReceivedDocument>(
    `/flowis/accounts/${accountGid}/received/${documentId}`,
    signal ? { signal } : {},
  );
}

// ---------------------------------------------------------------------------
// Inbound
// ---------------------------------------------------------------------------

/** What this service holds and nobody has confirmed taking yet. */
export function listInboundPending(participantId: string, signal?: AbortSignal): Promise<InboundDocument[]> {
  return apiJson<InboundDocument[]>(`${participantPath(participantId)}/inbound/pending`, {
    ...(signal ? { signal } : {}),
  });
}

/** What the gate is still holding — the question 'has anything been missed'. */
export function checkInboundWaiting(
  participantId: string,
  signal?: AbortSignal,
): Promise<InboundCheckResult> {
  return apiJson<InboundCheckResult>(`${participantPath(participantId)}/inbound/waiting`, {
    ...(signal ? { signal } : {}),
  });
}

export function acknowledgeInbound(
  participantId: string,
  receivedDocumentGid: string,
): Promise<InboundAckResult> {
  return apiJson<InboundAckResult>(`${participantPath(participantId)}/inbound/${receivedDocumentGid}/ack`, {
    method: 'POST',
  });
}

export function downloadInbound(
  participantId: string,
  flowisDocumentId: string,
  options: InboundDownloadOptions = {},
): Promise<InboundDownloadResult> {
  return apiJson<InboundDownloadResult>(
    `${participantPath(participantId)}/inbound/${flowisDocumentId}/download`,
    { method: 'POST', query: { includePdf: options.includePdf, markDownloaded: options.markDownloaded } },
  );
}

// ---------------------------------------------------------------------------
// Mappings
// ---------------------------------------------------------------------------

export function listMappings(type: MappingKind | undefined, signal?: AbortSignal): Promise<TenantMapping[]> {
  return apiJson<TenantMapping[]>('/mapping', {
    query: { type },
    ...(signal ? { signal } : {}),
  });
}

export function upsertMapping(request: UpsertTenantMappingRequest): Promise<TenantMapping> {
  return apiJson<TenantMapping>('/mapping', { method: 'PUT', body: request });
}

export function deleteMapping(mappingGid: string): Promise<void> {
  return apiVoid(`/mapping/${mappingGid}`, { method: 'DELETE' });
}

/** The tenant's mappings as a file, every kind or one — the shape importMappings takes back. */
export function exportMappings(
  type: MappingKind | undefined,
  signal?: AbortSignal,
): Promise<MappingsDocument> {
  return apiJson<MappingsDocument>('/mapping/export', {
    query: { type },
    ...(signal ? { signal } : {}),
  });
}

/** The same export as an Excel workbook; downloaded through downloadApiFile, which needs the path. */
export const mappingsWorkbookExportPath = '/mapping/export/xlsx';

export function importMappings(request: ImportMappingsRequest): Promise<ImportMappingsResult> {
  return apiJson<ImportMappingsResult>('/mapping/import', { method: 'POST', body: request });
}

/** An .xlsx workbook goes to the server as it is — only the server reads spreadsheets. */
export function importMappingsWorkbook(file: File, mode: MappingImportMode): Promise<ImportMappingsResult> {
  const body = new FormData();
  body.append('file', file);

  return apiJson<ImportMappingsResult>('/mapping/import/xlsx', { method: 'POST', body, query: { mode } });
}

export function listStandardCodes(type: MappingKind, signal?: AbortSignal): Promise<StandardCode[]> {
  return apiJson<StandardCode[]>('/mapping/standard-codes', {
    query: { type },
    ...(signal ? { signal } : {}),
  });
}

/** The active code list of one kind. 404 while none has been activated — callers treat that as "no list". */
export function getActiveCodelist(kind: MappingKind, signal?: AbortSignal): Promise<ActiveCodelist> {
  return apiJson<ActiveCodelist>('/mapping/active-codes', {
    query: { type: kind },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Administration
// ---------------------------------------------------------------------------

export function listTenants(signal?: AbortSignal): Promise<TenantSummary[]> {
  return apiJson<TenantSummary[]>('/admin/tenant', signal ? { signal } : {});
}

export function getTenant(tenantGid: string, signal?: AbortSignal): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/admin/tenant/${tenantGid}`, signal ? { signal } : {});
}

/** Live probes against the namespace and storage account — ask on demand, not on a poll. */
export function getTenantHealth(tenantGid: string, signal?: AbortSignal): Promise<TenantHealth> {
  return apiJson<TenantHealth>(`/admin/tenant/${tenantGid}/health`, signal ? { signal } : {});
}

export function createTenant(request: CreateTenantRequest): Promise<TenantDetail> {
  return apiJson<TenantDetail>('/admin/tenant', { method: 'POST', body: request });
}

export function updateTenant(tenantGid: string, request: UpdateTenantRequest): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/admin/tenant/${tenantGid}`, { method: 'PUT', body: request });
}

export function provisionTenantSchema(tenantGid: string): Promise<TenantDetail> {
  return apiJson<TenantDetail>(`/admin/tenant/${tenantGid}/schema`, { method: 'POST' });
}

/**
 * Renders the PowerShell provisioning (or check) script from the form's current values — nothing is
 * executed server-side; the administrator runs it under their own `az login`.
 */
export function generateTenantProvisioningScript(
  request: TenantProvisioningScriptRequest,
): Promise<TenantProvisioningScript> {
  return apiJson<TenantProvisioningScript>('/admin/tenant/provisioning-script', {
    method: 'POST',
    body: request,
  });
}

export function addTenantAccount(
  tenantGid: string,
  request: CreateFlowisAccountRequest,
): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>(`/admin/tenant/${tenantGid}/account`, {
    method: 'POST',
    body: request,
  });
}

export function updateTenantAccount(
  accountGid: string,
  request: UpdateFlowisAccountRequest,
): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>(`/admin/account/${accountGid}`, { method: 'PUT', body: request });
}

export function deleteTenantAccount(accountGid: string): Promise<void> {
  return apiVoid(`/admin/account/${accountGid}`, { method: 'DELETE' });
}

/** `unassigned` narrows to the pool (true) or to placed accounts (false); omit it for all of them. */
export function listAdminAccounts(unassigned?: boolean, signal?: AbortSignal): Promise<AdminFlowisAccount[]> {
  const query = unassigned === undefined ? '' : `?unassigned=${unassigned ? 'true' : 'false'}`;

  return apiJson<AdminFlowisAccount[]>(`/admin/account${query}`, signal ? { signal } : {});
}

/** `refresh` re-reads the identifiers from the gate before answering. */
export function getAdminAccount(
  accountGid: string,
  refresh = false,
  signal?: AbortSignal,
): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>(
    `/admin/account/${accountGid}${refresh ? '?refresh=true' : ''}`,
    signal ? { signal } : {},
  );
}

export function createAdminAccount(request: CreateAccountRequest): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>('/admin/account', { method: 'POST', body: request });
}

export function assignAccountTenant(accountGid: string, tenantGid: string): Promise<void> {
  return apiVoid(`/admin/account/${accountGid}/tenant/${tenantGid}`, { method: 'PUT' });
}

export function unassignAccountTenant(accountGid: string, tenantGid: string): Promise<void> {
  return apiVoid(`/admin/account/${accountGid}/tenant/${tenantGid}`, { method: 'DELETE' });
}

/** Lists what the global API pair can see at the gate; own-pair accounts are registered directly. */
export function listRemoteAccounts(signal?: AbortSignal): Promise<RemoteFlowisAccount[]> {
  return apiJson<RemoteFlowisAccount[]>('/admin/account/remote', signal ? { signal } : {});
}

/** Hands the account its own API client pair; the gate proves it before anything is stored. */
export function setAccountCredential(
  accountGid: string,
  request: SetAccountCredentialRequest,
): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>(`/admin/account/${accountGid}/credential`, {
    method: 'PUT',
    body: request,
  });
}

/** Drops the account's own pair, so it authenticates with the global one again. */
export function clearAccountCredential(accountGid: string): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>(`/admin/account/${accountGid}/credential`, { method: 'DELETE' });
}

export function createRemoteAccount(request: CreateRemoteAccountRequest): Promise<AdminFlowisAccount> {
  return apiJson<AdminFlowisAccount>('/admin/account/remote', { method: 'POST', body: request });
}

// --- Code lists ---

export function listCodelistVersions(
  kind: MappingKind | undefined,
  signal?: AbortSignal,
): Promise<CodelistVersion[]> {
  return apiJson<CodelistVersion[]>('/admin/codelist/version', {
    query: { kind },
    ...(signal ? { signal } : {}),
  });
}

export function importCodelist(request: ImportCodelistRequest): Promise<CodelistVersionDetail> {
  return apiJson<CodelistVersionDetail>('/admin/codelist/version', { method: 'POST', body: request });
}

export function getCodelistVersion(versionGid: string, signal?: AbortSignal): Promise<CodelistVersionDetail> {
  return apiJson<CodelistVersionDetail>(`/admin/codelist/version/${versionGid}`, signal ? { signal } : {});
}

export function replaceCodelistEntries(
  versionGid: string,
  request: ReplaceCodelistEntriesRequest,
): Promise<CodelistVersionDetail> {
  return apiJson<CodelistVersionDetail>(`/admin/codelist/version/${versionGid}/entries`, {
    method: 'PUT',
    body: request,
  });
}

export function getCodelistDiff(versionGid: string, signal?: AbortSignal): Promise<CodelistDiff> {
  return apiJson<CodelistDiff>(`/admin/codelist/version/${versionGid}/diff`, signal ? { signal } : {});
}

export function activateCodelist(versionGid: string): Promise<ActivateCodelistResult> {
  return apiJson<ActivateCodelistResult>(`/admin/codelist/version/${versionGid}/activate`, {
    method: 'POST',
  });
}

export function deleteCodelistDraft(versionGid: string): Promise<void> {
  return apiVoid(`/admin/codelist/version/${versionGid}`, { method: 'DELETE' });
}

export function listBrokenMappings(
  kind: MappingKind | undefined,
  signal?: AbortSignal,
): Promise<BrokenMappings[]> {
  return apiJson<BrokenMappings[]>('/admin/codelist/broken', {
    query: { kind },
    ...(signal ? { signal } : {}),
  });
}
